
import React, { Component, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { reportError } from '@/lib/errorReporting';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    console.error('AppErrorBoundary caught an error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔥 AppErrorBoundary: Critical error caught:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorInfo
    });
    reportError(error, {
      source: 'client_boundary',
      severity: 'fatal',
      componentStack: errorInfo.componentStack ?? undefined,
      context: { boundary: 'AppErrorBoundary' },
    });
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-muted/50">
          <Alert className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{this.props.fallbackTitle || 'Application Error'}</AlertTitle>
            <AlertDescription className="mt-2">
              {this.props.fallbackMessage || 'The application encountered an error. Please try refreshing the page.'}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-2 text-xs">
                  <summary>Error Details</summary>
                  <pre className="mt-1 overflow-auto">{this.state.error.message}</pre>
                </details>
              )}
            </AlertDescription>
            <div className="mt-4 flex gap-2">
              <Button 
                onClick={this.handleRetry}
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                variant="outline"
                size="sm"
              >
                Refresh Page
              </Button>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
